/**
 * Utilidades para dibujo en canvas
 */

/**
 * Dibuja una flecha en el canvas
 */
export const dibujarFlecha = (ctx, x1, y1, x2, y2, arrowLength = 8) => {
  const angle = Math.atan2(y2 - y1, x2 - x1)
  
  // Dibujar la línea principal
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()
  
  // Dibujar la punta de la flecha en el inicio
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(
    x1 + arrowLength * Math.cos(angle - Math.PI / 6),
    y1 + arrowLength * Math.sin(angle - Math.PI / 6)
  )
  ctx.moveTo(x1, y1)
  ctx.lineTo(
    x1 + arrowLength * Math.cos(angle + Math.PI / 6),
    y1 + arrowLength * Math.sin(angle + Math.PI / 6)
  )
  ctx.stroke()
  
  // Dibujar la punta de la flecha en el final
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(
    x2 - arrowLength * Math.cos(angle - Math.PI / 6),
    y2 - arrowLength * Math.sin(angle - Math.PI / 6)
  )
  ctx.moveTo(x2, y2)
  ctx.lineTo(
    x2 - arrowLength * Math.cos(angle + Math.PI / 6),
    y2 - arrowLength * Math.sin(angle + Math.PI / 6)
  )
  ctx.stroke()
}

/**
 * Dibuja un círculo de arrastre en un punto
 */
export const dibujarCirculoArrastre = (ctx, x, y, radio = 8, color = '#0066cc') => {
  ctx.fillStyle = color
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(x, y, radio, 0, 2 * Math.PI)
  ctx.fill()
  ctx.stroke()
}

/**
 * Dibuja una línea con estilo
 */
export const dibujarLinea = (ctx, x1, y1, x2, y2, options = {}) => {
  const {
    color = '#800080',
    lineWidth = 1,
    dash = []
  } = options

  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth
  ctx.setLineDash(dash)
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()
}

/**
 * Calcula la distancia de un punto a una línea
 */
export const distanciaPuntoALinea = (px, py, x1, y1, x2, y2) => {
  const A = px - x1
  const B = py - y1
  const C = x2 - x1
  const D = y2 - y1

  const dot = A * C + B * D
  const lenSq = C * C + D * D
  let param = -1
  if (lenSq !== 0) param = dot / lenSq

  let xx, yy

  if (param < 0) {
    xx = x1
    yy = y1
  } else if (param > 1) {
    xx = x2
    yy = y2
  } else {
    xx = x1 + param * C
    yy = y1 + param * D
  }

  const dx = px - xx
  const dy = py - yy
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Calcula la distancia de un punto a un círculo
 */
export const distanciaPuntoACirculo = (px, py, cx, cy, radio) => {
  const distancia = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2)
  return Math.abs(distancia - radio)
}

/**
 * Normaliza coordenadas del canvas (de píxeles a 0-1000)
 */
export const normalizarCoordenadas = (x, y, canvas) => {
  const escalaX = 1000 / canvas.width
  const escalaY = 1000 / canvas.height
  return {
    x: x * escalaX,
    y: y * escalaY
  }
}

/**
 * Escala coordenadas normalizadas a píxeles del canvas
 */
export const escalarCoordenadas = (x, y, canvas) => {
  const escalaX = canvas.width / 1000
  const escalaY = canvas.height / 1000
  return {
    x: x * escalaX,
    y: y * escalaY
  }
}

/** Tamaño de exportación para ecommerce (caja de fotos Axioma). */
export const ECOMMERCE_IMAGE_SIZE = 1000

/**
 * Exporta una imagen a cuadrado size×size (contain + fondo blanco) para ecommerce.
 * Devuelve un Blob JPEG.
 */
export const exportarImagenEcommerce = async (
  imageUrl,
  size = ECOMMERCE_IMAGE_SIZE,
) => {
  const response = await fetch(imageUrl, {
    mode: 'cors',
    credentials: 'omit',
  })
  if (!response.ok) {
    throw new Error('No se pudo obtener la imagen')
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new window.Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('No se pudo cargar la imagen'))
      image.src = objectUrl
    })

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, size, size)

    const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight)
    const drawW = img.naturalWidth * scale
    const drawH = img.naturalHeight * scale
    const x = (size - drawW) / 2
    const y = (size - drawH) / 2
    ctx.drawImage(img, x, y, drawW, drawH)

    const outBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result)
          else reject(new Error('No se pudo exportar la imagen'))
        },
        'image/jpeg',
        0.92,
      )
    })
    return outBlob
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Nombre de archivo para la exportación ecommerce (fuerza .jpg y sufijo 1000x1000).
 */
export const nombreArchivoEcommerce = (imageName, size = ECOMMERCE_IMAGE_SIZE) => {
  const base = String(imageName || 'imagen')
    .replace(/\.[^.]+$/, '')
    .trim() || 'imagen'
  return `${base}_${size}x${size}.jpg`
}

